/*
 * Drives the theme-native mobile sticky add-to-cart bar (<sticky-atc>).
 *  - Relocates itself to <body> so `position: fixed` is never trapped by a transformed
 *    / overflow-hidden ancestor (Dawn's scroll-reveal wrappers use transform).
 *  - Mirrors the main product price into the bar (covers variant + app/bundle price changes).
 *  - Shows the bar on mobile once the real add-to-cart button scrolls out of view.
 * Quantity +/- and the add-to-cart submit are handled by existing theme code
 * (global.js QuantityInput + product-form.js), so this file only wires layout/price/visibility.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.mobileQuery = window.matchMedia('(max-width: 1024px)');
        this.onScroll = this.onScroll.bind(this);
        this.evaluate = this.evaluate.bind(this);
        this.ticking = false;
      }

      connectedCallback() {
        // Move to <body> once, so fixed positioning is relative to the viewport.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this); // re-triggers connectedCallback with body as parent
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        this.stickyPrice = this.querySelector('#price-sticky-' + this.sectionId);
        this.mainPrice = document.getElementById('price-' + this.sectionId);
        this.trigger = this.findTrigger();

        this.setupPriceMirror();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.evaluate);
        this.evaluate();
      }

      /* The real add-to-cart button (falls back through a few selectors). */
      findTrigger() {
        return (
          document.getElementById('ProductSubmitButton-' + this.sectionId) ||
          document.querySelector('#MainProduct-' + this.sectionId + ' .product-form__submit') ||
          document.querySelector('.product-form__submit') ||
          document.getElementById('product-form-' + this.sectionId)
        );
      }

      /* Keep the bar price identical to the main price block. */
      setupPriceMirror() {
        if (!this.stickyPrice || !this.mainPrice) return;
        this.syncPrice();
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.mainPrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncPrice() {
        this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.evaluate();
          this.ticking = false;
        });
      }

      /* Reveal the bar (mobile only) once the real add-to-cart button is scrolled past. */
      evaluate() {
        if (!this.mobileQuery.matches) {
          this.classList.remove('sticky-atc--active');
          return;
        }
        if (!this.trigger || !this.trigger.isConnected) this.trigger = this.findTrigger();

        let passed = window.scrollY > 350; // fallback if the button can't be located
        if (this.trigger) {
          const rect = this.trigger.getBoundingClientRect();
          passed = rect.bottom < 8; // button scrolled above the top of the viewport
        }
        this.classList.toggle('sticky-atc--active', passed);
      }
    }
  );
}
